/**
 * AJUSTES DE LA APP (punto 64 del panel).
 *
 * «Sacar los interruptores escondidos a una pantalla de ajustes: los correos y la frase del
 * día están dentro de Planes.»
 *
 * Y era verdad: para apagar una pantalla a todos los clientes, o para cambiar la frase del
 * día, había que entrar en «Planes» -- que es el catálogo de lo que se vende -- y bajar
 * hasta el final. Nada de esto es un plan. Ahora tienen su sitio y se llega por el menú.
 *
 * SE MUEVE LO GLOBAL, Y SOLO LO GLOBAL. «Mi modo pruebas» se queda en Planes a propósito:
 * no es un ajuste de la app sino de TU cuenta (te la cambia de estado y hay que restaurarla
 * después), y mezclarlo aquí invita justo al accidente que esta pantalla debe evitar --
 * creer que tocas lo tuyo cuando lo tocas para todos.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { SlidersHorizontal, AlertTriangle } from 'lucide-react';
import { PANTALLAS_APP, MANDA_CORREOS } from '../lib/pantallasDeLaApp';
import PuntoEncendido from '../components/PuntoEncendido';
import HelpTooltip from '../components/HelpTooltip';
import { mensajeDeError } from '../lib/mensajeDeError';
import { useConfirm } from '../components/ui/confirm';
import { ahoraDeLaApp } from '../lib/horaEspana';

const AdminAjustesPage = () => {
    const { api } = useAuth();
    const [ajustes, setAjustes] = useState(null);
    const [frase, setFrase] = useState('');
    const [fechaFrase, setFechaFrase] = useState('');
    const [guardandoFrase, setGuardandoFrase] = useState(false);
    // EL WHATSAPP DEL ENTRENADOR (ficha 89 del parte de Gonzalo, 4-09). La tarjeta «Tu
    // seguimiento» del Chat manda al Premium a WhatsApp y no había número en ningún
    // sitio. Se pone aquí, para todos, y el botón del Chat sale solo cuando hay número.
    const [whatsapp, setWhatsapp] = useState('');
    const [guardandoWhatsapp, setGuardandoWhatsapp] = useState(false);

    useEffect(() => {
        api.get('/admin/settings')
            .then((res) => { setAjustes(res.data || null); setWhatsapp(res.data?.whatsapp_entrenador || ''); })
            .catch(() => toast.error('No se pudieron cargar los ajustes de la app'));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al entrar
    }, []);

    const guardarWhatsapp = async () => {
        setGuardandoWhatsapp(true);
        try {
            const res = await api.put('/admin/settings', { whatsapp_entrenador: whatsapp.trim() });
            setAjustes(res.data);
            setWhatsapp(res.data?.whatsapp_entrenador || '');
            toast.success(res.data?.whatsapp_entrenador ? 'Número guardado' : 'Número quitado: el botón del Chat no sale');
        } catch (e) {
            toast.error(mensajeDeError(e, 'No se pudo guardar el número'));
        } finally {
            setGuardandoWhatsapp(false);
        }
    };

    const { confirm } = useConfirm();
    // UN INTERRUPTOR DE ESTOS ES PARA TODOS LOS CLIENTES, AL INSTANTE (14-10): se cambiaba
    // al primer clic y sin aviso (apagar «Entreno» les quitaba la rutina a todos). Se
    // pregunta en los dos sentidos, con el mismo diálogo que el resto del panel.
    const alternar = async (clave) => {
        const nuevo = !ajustes?.pantallas?.[clave];
        const cual = PANTALLAS_APP.find((p) => p.clave === clave);
        if (!await confirm({
            title: `¿${nuevo ? 'Encender' : 'Apagar'} «${cual?.label || clave}» para todos?`,
            description: `${cual?.ayuda ? `${cual.ayuda} ` : ''}El cambio les llega a todos los clientes al momento. Puedes deshacerlo con otro clic.`,
            confirmLabel: nuevo ? 'Encender' : 'Apagar', danger: !nuevo,
        })) return;
        try {
            const res = await api.put('/admin/settings', { pantallas: { [clave]: nuevo } });
            setAjustes(res.data);
        } catch (e) {
            toast.error('No se pudo guardar el cambio');
        }
    };

    const guardarFrase = async () => {
        if (!frase.trim()) return;
        setGuardandoFrase(true);
        try {
            const res = await api.put('/admin/settings', {
                frase_del_dia: { texto: frase.trim(), ...(fechaFrase ? { fecha: fechaFrase } : {}) },
            });
            setAjustes(res.data);
            setFrase('');
            setFechaFrase('');
            toast.success(fechaFrase ? 'Frase programada' : 'Frase del día guardada');
        } catch (e) {
            toast.error('No se pudo guardar la frase');
        } finally {
            setGuardandoFrase(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="admin-ajustes">
            <div className="flex items-center gap-3">
                <SlidersHorizontal className="w-6 h-6 text-[#FF671F]" />
                <h1 className="text-xl font-bold text-white">Ajustes de la app</h1>
            </div>

            {!ajustes ? (
                <p className="text-sm text-white/40">Cargando los ajustes...</p>
            ) : (
                <>
                    <Card className="bg-[#111] border-[#2a2a2a]">
                        <CardContent className="p-4 space-y-4">
                            <h2 className="text-base font-bold text-white">Pantallas de la app</h2>
                            {/* La ayuda de cada interruptor SE VE. Estaba escrita desde el
                                principio pero solo la enseñaba «Mi modo pruebas»: aquí, que
                                es donde se enciende para todo el mundo, salía a ciegas. */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                                {PANTALLAS_APP.map(({ clave, label, ayuda }) => {
                                    const on = !!ajustes.pantallas?.[clave];
                                    return (
                                        <div key={clave} className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => alternar(clave)}
                                                data-testid={`ajuste-${clave}`}
                                                className={`flex-1 min-w-0 flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors ${on ? 'border-green-500/40 bg-green-500/10 text-white' : 'border-[#333] bg-black/30 text-white/60'}`}
                                            >
                                                <span>{label}</span>
                                                <PuntoEncendido on={on} />
                                            </button>
                                            <HelpTooltip text={ayuda} className="shrink-0" />
                                        </div>
                                    );
                                })}
                            </div>
                            {/* EL QUE MANDABA CORREOS YA NO ESTÁ AQUÍ (Francisco, 14-09:
                                «oculta el interruptor»). Los de arriba encienden o apagan una
                                pantalla y se deshacen con otro clic; ese mandaba correos de
                                verdad a gente de verdad, y no hay forma de recogerlos.
                                El cartel se queda, porque el interruptor sigue existiendo y
                                alguien tiene que poder saber en qué estado está y por qué no
                                se ve. Desde el 17-10 no enciende nada: se fue la pasada de
                                correos por calendario (`core/correo_avisos`). */}
                            <div className="flex gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-3 py-2"
                                 data-testid="aviso-correos-oculto">
                                <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
                                <p className="text-xs text-white/70">
                                    «Los avisos del reporte, por correo» ya no manda nada: desde el 17-10 los
                                    correos del ciclo salen de las reglas del reloj, no de los avisos de la app.
                                    {ajustes.pantallas?.[MANDA_CORREOS]
                                        ? ' La llave sigue ENCENDIDA en la base, sin efecto.'
                                        : ' La llave sigue apagada en la base, sin efecto.'}
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-[#111] border-[#2a2a2a]">
                        <CardContent className="p-4 space-y-1">
                            <Label className="text-xs text-white/60">
                                Frase del día
                                {ajustes.frase_del_dia?.texto ? (
                                    <span className="ml-2 text-white/40 normal-case">ahora: «{ajustes.frase_del_dia.texto}» ({ajustes.frase_del_dia.fecha})</span>
                                ) : (
                                    <span className="ml-2 text-white/40">todavía no hay ninguna</span>
                                )}
                            </Label>
                            <div className="flex gap-2">
                                <Input
                                    value={frase}
                                    onChange={(e) => setFrase(e.target.value)}
                                    placeholder="El único secreto que tiene esto es no dejarlo."
                                    className="bg-black/30 border-[#333] text-white text-sm"
                                />
                                {/* PROGRAMABLE CON UNA SEMANA (doc 19-08): sin fecha entra hoy;
                                    con fecha se queda en la cola y sale su día sola. */}
                                <Input
                                    type="date"
                                    value={fechaFrase}
                                    onChange={(e) => setFechaFrase(e.target.value)}
                                    min={ahoraDeLaApp().toLocaleDateString('en-CA')}
                                    max={new Date(Date.now() + 7 * 864e5).toLocaleDateString('en-CA')}
                                    className="bg-black/30 border-[#333] text-white text-sm w-40 shrink-0"
                                />
                                <Button onClick={guardarFrase} disabled={guardandoFrase || !frase.trim()} className="bg-[#FF671F] hover:bg-[#e55b1a] text-white">
                                    {fechaFrase ? 'Programar' : 'Guardar'}
                                </Button>
                            </div>
                            {(ajustes.frases_programadas || []).length > 0 && (
                                <div className="space-y-0.5 pt-1">
                                    {ajustes.frases_programadas.map((f) => (
                                        <p key={f.fecha} className="text-[11px] text-white/50">
                                            {f.fecha} · «{f.texto}»
                                        </p>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="bg-[#111] border-[#2a2a2a]">
                        <CardContent className="p-4 space-y-1">
                            <Label className="text-xs text-white/60">
                                WhatsApp del entrenador
                                {ajustes.whatsapp_entrenador ? (
                                    <span className="ml-2 text-white/40 normal-case">ahora: +{ajustes.whatsapp_entrenador}</span>
                                ) : (
                                    <span className="ml-2 text-white/40">sin número</span>
                                )}
                            </Label>
                            <div className="flex gap-2">
                                <Input
                                    value={whatsapp}
                                    onChange={(e) => setWhatsapp(e.target.value)}
                                    placeholder="34 600 000 000"
                                    inputMode="tel"
                                    data-testid="ajuste-whatsapp"
                                    className="bg-black/30 border-[#333] text-white text-sm"
                                />
                                <Button onClick={guardarWhatsapp} disabled={guardandoWhatsapp}
                                    data-testid="ajuste-whatsapp-guardar"
                                    className="bg-[#FF671F] hover:bg-[#e55b1a] text-white">
                                    Guardar
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default AdminAjustesPage;
